/** @format */

import axios from "axios";
import type { ApiError } from "@/api/axiosClient.ts";
import { ApiErrorResponse } from "@/api/axiosClient.ts";

// Common pagination shape used by list endpoints
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Shared error handler for API modules.
 * Normalizes Axios errors and rethrows consistent ApiErrorResponse.
 */
export function handleApiError(error: unknown, defaultMessage: string): never {
  if (error instanceof ApiErrorResponse) {
    throw error;
  }
  if (axios.isAxiosError(error)) {
    const errorData = (error.response?.data as ApiError) || {};
    throw new ApiErrorResponse(
      (errorData as any).detail || defaultMessage,
      error.response?.status || 0,
      errorData
    );
  }
  throw error as any;
}


// Common query params used by list endpoints
export interface PaginationParams {
  page?: number;
  page_size?: number;
  ordering?: string;
  search?: string;
}

// Re-export error types for convenience in API modules
export { ApiErrorResponse };
export type { ApiError };

// Pagination helpers shared across hooks/components.
export function clampPage(totalCount: number, pageSize: number, requestedPage: number): number {
  const safePageSize = pageSize || 10;
  const totalPages = Math.ceil((totalCount || 0) / safePageSize);
  if (totalPages === 0) return 1; // normalize empty lists to page 1
  const page = Math.max(1, Math.min(requestedPage || 1, totalPages));
  return page;
}

// Given a paginated response, return the clamped page we should be on after fetch.
export function resolvePageAfterFetch<T>(
  response: PaginatedResponse<T> | null | undefined,
  pageSize: number,
  requestedPage: number
): number {
  const totalCount = response?.count ?? 0;
  return clampPage(totalCount, pageSize, requestedPage);
}

// Deduplicate array items by a stable identifier (id | _id | uuid), with JSON fallback.
export function dedupeByStableId<T extends Record<string, any>>(items: T[] = []): T[] {
  const seen = new Set<string | number>();
  const result: T[] = [];
  for (const it of items) {
    const key = (it as any)?.id ?? (it as any)?._id ?? (it as any)?.uuid ?? JSON.stringify(it);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(it);
  }
  return result;
}
